// SETTINGS V2 — Change-password dialog (SETTINGS_V2_SP2 §7.2-§7.5).
//
// First consumer of SettingsV2DialogShell. Binds the reused customerAccount
// slice (data slices are UNCHANGED — v2 is a view swap). Open-state lives in
// settingsV2UiSlice.openDialog === "changePassword" (the SP0 seam). The success
// toast renders via the PAGE's ActionToast — this dialog mounts no toast of its
// own (§6.4 dedupe).
//
// PARITY (§7.3): payload is EXACTLY { oldPassword, newPassword } — confirmPassword
// is a CLIENT-ONLY match check, never sent. Validators ported verbatim from the
// legacy ChangeAccountPasswordDialogue. Visibility toggles stay in REDUX.
//
// USER-APPROVED IN-SP2 FIXES:
// • §7.4 submit-enabled: the button starts DISABLED, enabling only when all three
//   fields are non-empty (the legacy RHF `watch(...) === ""` bug enabled it on
//   first open). Changes ONLY the button's enabled state; validators unchanged.
// • Q-SETV2-9: force all three visibility flags back to MASKED on OPEN, so a field
//   never starts as plaintext on reopen (was "not reset" in the spec).
//
// Styling: inline styles + scoped CSS-var tokens + the v2 atoms. No MUI/sx/dt/
// Tailwind. SEC-REQ-3: password values live only in transient local state + the
// HTTPS request body — never logged, never in Redux, never in storage.
import React, { useCallback, useEffect, useRef, useState } from "react";

import {
  useAppDispatch,
  useAppSelector,
} from "../../../../shared/hooks/sharedHooks";
import useTranslation from "../../../../shared/hooks/useTranslation";
import { passwordRegex } from "../../../../shared/validators";
import {
  changePassword,
  clearError,
  clearSuccess,
  toggleOldPasswordVisibility,
  toggleNewPasswordVisibility,
  toggleConfirmPasswordVisibility,
} from "../../customerAccountSlice";
import { closeDialog } from "../settingsV2UiSlice";
import { SInputField } from "../atoms";
import SettingsV2DialogShell from "./SettingsV2DialogShell";

// 16px inline-SVG eye / eye-off (stroke 1.5, currentColor) — no MUI icons (§9).
const EyeGlyph: React.FC<{ shown: boolean }> = ({ shown }) => (
  <svg
    width={16}
    height={16}
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth={1.5}
    strokeLinecap="round"
    strokeLinejoin="round"
    aria-hidden="true"
  >
    {shown ? (
      <>
        <path d="M1 12s4-7 11-7 11 7 11 7-4 7-11 7-11-7-11-7z" />
        <circle cx="12" cy="12" r="3" />
      </>
    ) : (
      <>
        <path d="M9.9 4.24A9.1 9.1 0 0 1 12 4c7 0 11 8 11 8a18.4 18.4 0 0 1-2.16 3.19M6.61 6.61A13.5 13.5 0 0 0 1 12s4 8 11 8a9.7 9.7 0 0 0 5.39-1.61" />
        <path d="M14.12 14.12a3 3 0 1 1-4.24-4.24" />
        <path d="M1 1l22 22" />
      </>
    )}
  </svg>
);

// Eye button — className carries the scoped hover/focus rules (§9). onMouseDown
// preventDefault keeps focus in the input on click (legacy parity).
const EyeButton: React.FC<{ shown: boolean; label: string; onClick: () => void }> = ({
  shown,
  label,
  onClick,
}) => (
  <button
    type="button"
    className="sv2-eye-btn"
    aria-label={label}
    aria-pressed={shown}
    onClick={onClick}
    onMouseDown={(e) => e.preventDefault()}
  >
    <EyeGlyph shown={shown} />
  </button>
);

const ChangePasswordDialogV2: React.FC = () => {
  const dispatch = useAppDispatch();
  const { translate: tAccount } = useTranslation("account");
  const { translate: tCommon } = useTranslation("common");
  const { translate: tSettings } = useTranslation("settings");

  const open = useAppSelector(
    (s) => s.settingsV2Ui.openDialog === "changePassword"
  );
  const {
    isSaving,
    success,
    showOldPassword,
    showNewPassword,
    showConfirmPassword,
  } = useAppSelector((s) => s.customerAccount);

  const [oldPassword, setOldPassword] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [touched, setTouched] = useState<Record<string, boolean>>({});
  const markTouched = (f: string) =>
    setTouched((t) => ({ ...t, [f]: true }));

  const onClose = useCallback(() => dispatch(closeDialog()), [dispatch]);

  // Q-SETV2-9 (user-approved in-SP2 fix): force all three fields back to MASKED
  // on OPEN. Only toggle actions exist in the slice, so flip only the currently-
  // shown ones. The latest flags are read from a ref so this effect depends on
  // [open] alone — depending on the flags would re-fire it on every eye click and
  // defeat the toggles.
  const showRef = useRef({ o: showOldPassword, n: showNewPassword, c: showConfirmPassword });
  showRef.current = { o: showOldPassword, n: showNewPassword, c: showConfirmPassword };
  useEffect(() => {
    if (!open) return;
    if (showRef.current.o) dispatch(toggleOldPasswordVisibility());
    if (showRef.current.n) dispatch(toggleNewPasswordVisibility());
    if (showRef.current.c) dispatch(toggleConfirmPasswordVisibility());
  }, [open, dispatch]);

  // On password-change success while open: close the dialog + clear success.
  // The success TOAST itself renders via the page's ActionToast (slice sets
  // openSnackbar). Gated on the "passwordChanged" value so a lingering
  // "accountUpdated" success from a prior profile save never auto-closes us.
  useEffect(() => {
    if (open && success === "passwordChanged") {
      onClose();
      dispatch(clearSuccess());
    }
  }, [open, success, onClose, dispatch]);

  // On close: reset the 3 local fields + touched, clearError, clearSuccess
  // (legacy lines 79-85). Visibility flags stay in Redux and are NOT reset
  // (parity, Q-SETV2-9).
  useEffect(() => {
    if (!open) {
      setOldPassword("");
      setNewPassword("");
      setConfirmPassword("");
      setTouched({});
      dispatch(clearError());
      dispatch(clearSuccess());
    }
  }, [open, dispatch]);

  // Confirm-match (legacy validateConfirmPassword lines 69-75): valid when
  // EITHER value is empty/whitespace; only flags when both non-empty and differ.
  const confirmMatches =
    !confirmPassword ||
    confirmPassword.trim() === "" ||
    !newPassword ||
    newPassword.trim() === "" ||
    newPassword === confirmPassword;

  const requirementsMsg = tAccount("passwordRequirements");
  const toggleVisibilityLabel = tSettings("settings:togglePasswordVisibility");

  const oldError =
    touched.oldPassword && oldPassword === ""
      ? tAccount("fieldRequired")
      : undefined;
  const newError = touched.newPassword
    ? newPassword === ""
      ? tAccount("fieldRequired")
      : !passwordRegex.test(newPassword)
      ? requirementsMsg
      : undefined
    : undefined;
  const confirmError = touched.confirmPassword
    ? confirmPassword === ""
      ? tAccount("fieldRequired")
      : !confirmMatches
      ? tAccount("passwordsDoNotMatch")
      : undefined
    : undefined;

  // FLAGGED FIX (§7.4): starts disabled; enables only when all 3 non-empty.
  const submitEnabled =
    oldPassword !== "" && newPassword !== "" && confirmPassword !== "";

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    setTouched({ oldPassword: true, newPassword: true, confirmPassword: true });
    if (
      oldPassword === "" ||
      newPassword === "" ||
      confirmPassword === "" ||
      !passwordRegex.test(newPassword) ||
      !confirmMatches
    ) {
      return;
    }
    dispatch(changePassword({ oldPassword, newPassword }));
  };

  const footer = (
    <>
      <button type="button" className="btn btn-ghost" onClick={onClose}>
        {tCommon("buttons.cancel")}
      </button>
      <button
        type="submit"
        form="changePasswordFormV2"
        className="btn btn-primary"
        disabled={!submitEnabled}
        style={{ background: "var(--mod-accent)", borderColor: "var(--mod-accent)" }}
      >
        {tAccount("changePassword")}
      </button>
    </>
  );

  return (
    <SettingsV2DialogShell
      open={open}
      onClose={onClose}
      width={560}
      title={tAccount("changePassword")}
      labelledById="sv2-change-password-title"
      busy={isSaving}
      footer={footer}
    >
      <form
        id="changePasswordFormV2"
        onSubmit={handleSubmit}
        style={{ display: "flex", flexDirection: "column", gap: 12 }}
      >
        <div onBlur={() => markTouched("oldPassword")}>
          <SInputField
            label={tAccount("currentPassword")} required mono
            type={showOldPassword ? "text" : "password"}
            value={oldPassword} onChange={setOldPassword} error={oldError}
            endAdornment={<EyeButton shown={showOldPassword} label={toggleVisibilityLabel} onClick={() => dispatch(toggleOldPasswordVisibility())} />}
          />
        </div>

        <div onBlur={() => markTouched("newPassword")}>
          <SInputField
            label={tAccount("newPassword")} required mono
            type={showNewPassword ? "text" : "password"}
            value={newPassword} onChange={setNewPassword} error={newError}
            endAdornment={<EyeButton shown={showNewPassword} label={toggleVisibilityLabel} onClick={() => dispatch(toggleNewPasswordVisibility())} />}
          />
        </div>

        {/* Static regex-hint line — hidden when newPassword already shows the
            same message as an error (avoids the doubled sentence, §7.2). */}
        {newError !== requirementsMsg && (
          <div style={{ fontSize: 11.5, color: "var(--muted)", lineHeight: 1.55, margin: "-4px 0" }}>
            {requirementsMsg}
          </div>
        )}

        <div onBlur={() => markTouched("confirmPassword")}>
          <SInputField
            label={tAccount("confirmNewPassword")} required mono
            type={showConfirmPassword ? "text" : "password"}
            value={confirmPassword} onChange={setConfirmPassword} error={confirmError}
            endAdornment={<EyeButton shown={showConfirmPassword} label={toggleVisibilityLabel} onClick={() => dispatch(toggleConfirmPasswordVisibility())} />}
          />
        </div>
      </form>
    </SettingsV2DialogShell>
  );
};

export default ChangePasswordDialogV2;
